import 'ol/ol.css'
import './style/style.scss'
import Map from './map';
import Layer from './layer';
import Projection from './projection';
import Control from './control';
import Geolocation from './geolocation';
import Style from './style';
import Edit from './edit';
import Overlay from './overlay';
import Interaction from './interaction';
import Analysis from './analysis';
import Topology from './topology';
import Offline from './offline';
import {Localisation} from './general/localisation';
import {ViewConfiguration} from "./configuration/view";
import {MapConfiguration} from "./configuration/map";
import {StyleConfiguration} from "./configuration/style";
import {ControlConfiguration} from "./configuration/control";
import {ProjectionConfiguration} from "./configuration/projection";
import {LayerConfiguration} from "./configuration/layer";
import {GroupConfiguration} from "./configuration/group";
import View from "./view";

export interface Configuration {
    projection: ProjectionConfiguration;
    map: MapConfiguration;
    style: StyleConfiguration[];
    view: ViewConfiguration;
    layer: LayerConfiguration[];
    group: GroupConfiguration[];
    control: ControlConfiguration[];
}

type DestroyOptions = {
    /**
     * Preserve the original map element content. Default is false.
     */
    preserveMapElementContent: boolean;
}

class GIS {
    private static counter = 0;
    private instanceId: number = 0;
    public Map: Map;
    public View: View;
    public Layer: Layer;
    public Projection: Projection;
    public Control: Control;
    public Geolocation: Geolocation;
    public Style: Style;
    public Edit: Edit;
    public Overlay: Overlay;
    public Interaction: Interaction;
    public Analysis: Analysis;
    /**
     * Geometry validation and topology repairs for features already on a layer —
     * spike detection and removal, sliver / degeneracy cleaning, and the
     * validate-before-you-save gate. See {@link Topology}.
     */
    public Topology: Topology;
    public Offline: Offline;
    public Localisation: Localisation;
    private configuration: Configuration;

    constructor(configuration: Configuration) {
        this.configuration = configuration;

        this.Localisation = new Localisation();
        this.Projection = new Projection(this);
        this.Style = new Style();
        this.Layer = new Layer(this);
        this.Interaction = new Interaction(this);
        this.Control = new Control(this);
        this.Geolocation = new Geolocation(this);
        this.Edit = new Edit(this);
        this.Overlay = new Overlay(this);
        this.Analysis = new Analysis(this);
        this.Topology = new Topology(this);
        this.Offline = new Offline(this);
        this.View = new View(this);
        this.Map =  new Map(this);
    }
    private initializedModules: Set<string> = new Set();

    /**
     * Initializes the GIS modules in a predefined order up to and including the specified modules.
     *
     * Modules are initialized according to a preferred order:
     * `["projection", "style", "layer", "interaction", "view", "map", "control"]`
     *
     * If a subset is passed (e.g., `["view"]`), all modules in the preferred order **up to and including**
     * `"view"` will be initialized (`"projection"`, `"style"`, `"layer"`, `"interaction"`, `"view"`).
     *
     * This method is **idempotent** – modules already initialized in a previous `init()` call will be skipped.
     * You can safely call `init()` multiple times with increasing subsets or the full list, and it will continue
     * initializing the remaining modules.
     *
     * @param modulesToInit - A list of module names to initialize up to. Defaults to all available modules.
     *                        Example: `["view"]` initializes all modules up to "view".
     * @param options - Optional initialization options.
     * @param options.sync - When `false` (default), `init()` awaits any internal async work
     *   (currently the icon preload inside `Style.init`) so the map renders with all icons
     *   already cached. When `true`, all such async work is fired in the background and
     *   `init()` completes without waiting on any network IO — required for callers that
     *   cannot observe a returned Promise (e.g. Android WebView `evaluateJavascript`).
     *   With `sync: true`, point icons may render empty for a short moment before OL
     *   re-renders them as each image loads. Future async init steps should also respect
     *   this flag.
     *
     * @returns The unique `instanceId` of this GIS instance.
     *
     * @example
     * // Initialize only up to 'view' (also includes projection, style, layer, interaction)
     * gis.init(["view"]);
     *
     * // Later call completes the remaining initialization up to 'map'
     * gis.init(["map"]);
     *
     * // Or just call init() to ensure full initialization
     * gis.init();
     *
     * // Mobile / WebView: do not await any async init work
     * gis.init(undefined, { sync: true });
     */
    async init(modulesToInit: string[] = [
        "projection",
        "style",
        "layer",
        "view",
        "map",
        "control",
        "interaction"
    ], options?: { sync?: boolean }): Promise<number> {
        const sync = options?.sync ?? false;

        GIS.counter++;
        this.instanceId = GIS.counter;

        const toInitialize = this.resolveModulesByPreferredOrder(modulesToInit);

        for (const module of toInitialize) {
            if (this.initializedModules.has(module)) continue;

            switch (module) {
                case "projection":
                    this.Projection.init(this.configuration.projection);
                    break;
                case "style":
                    await this.Style.init(this.configuration.style, !sync);
                    break;
                case "layer":
                    this.Layer.init(this.configuration.layer, this.configuration.group);
                    break;
                case "interaction":
                    this.Interaction.init(this.configuration.map);
                    break;
                case "view":
                    this.View.init(this.configuration.view);
                    break;
                case "map":
                    this.Map.init(this.configuration.map);
                    break;
                case "control":
                    this.Control.init(this.configuration.control);
                    break;
                default:
                    console.warn(`Unknown or unimplemented module: ${module}`);
            }

            this.initializedModules.add(module);
        }

        return this.instanceId;
    }

    private static preferredOrder = [
        "projection",
        "style",
        "layer",
        "interaction",
        "view",
        "map",
        "control"
    ];

    private resolveModulesByPreferredOrder(modules: string[]): string[] {
        const indices = modules.map(m => GIS.preferredOrder.indexOf(m));
        const maxIndex = Math.max(...indices);

        return GIS.preferredOrder.slice(0, maxIndex + 1);
    }

    /**
     * Returns the set of initialized modules for this GIS instance.
     *
     * This can be useful for debugging or checking which parts of the GIS system
     * have been successfully initialized.
     *
     * @returns A Set containing the names of initialized modules (e.g., `["projection", "style", "layer"]`).
     *
     * @example
     * gis.init();
     * const modules = gis.getInitializedModules(); // e.g., Set { "projection", "style", "layer", "view", "map", "control" }
     */
    public getInitializedModules(): Set<string> {
        return this.initializedModules;
    }

    /**
     * Returns the unique instance ID of this GIS object.
     *
     * Each GIS instance gets a sequential ID when `init()` is called.
     * This is useful for debugging, logging, or managing multiple map instances.
     *
     * @returns The numeric ID of this GIS instance (e.g., 1, 2, 3...).
     *
     * @example
     * gis.init();
     * const id = gis.getInstanceCounter(); // e.g., 1
     */
    public getInstanceCounter(): number {
        return this.instanceId;
    }

    /**
     * Set the active UI locale (BCP-47, e.g. `"en-US"`, `"cs-CZ"`, `"de-DE"`).
     *
     * Every label / tooltip registered through {@link Localisation}
     * (layer-switcher titles, group titles, button tooltips, panel headers,
     * measurement / legend / geolocation tips, etc.) is updated in place.
     *
     * Pass an empty string to fall back to the default strings declared in the
     * configuration (`switcher.title`, `options.tip`, `group.title`, ...).
     *
     * @example
     * gis.setLocalisation('cs-CZ');
     */
    public setLocalisation(locale: string): void {
        this.Localisation.setLocale(locale);
    }

    /** @returns The active locale, or empty string if none is set. */
    public getLocalisation(): string {
        return this.Localisation.getLocale();
    }

    /**
     * Destroys the current GIS instance by cleaning up modules and optionally clearing the map DOM content.
     *
     * This method:
     * - Clears state from modules like Geolocation, Control, Interaction, Layer, Projection, and Style.
     * - Removes all child elements from the map container element, unless `preserveMapElementContent` is set.
     * - Unsets internal OpenLayers references (`Map.olObject`) and registered listeners.
     *
     * This is useful when dynamically creating and removing GIS instances (e.g., in single-page apps),
     * or when resetting the map completely.
     *
     * The method is safe to call more than once. Because it is typically invoked from a component
     * destructor, consumers cannot always prevent a duplicate call — a repeated or late call logs a
     * console warning and returns instead of throwing. Any unexpected error raised while tearing the
     * modules down is caught and reported as a warning so it cannot break the host application.
     *
     * @param options - Optional configuration for destroy behavior.
     * @param options.preserveMapElementContent - If `true`, the content of the original map element will be preserved.
     *                                            If `false` (default), the map container will be emptied.
     *
     * @example
     * // Destroy the instance and clear map container
     * gis.destroy();
     *
     * // Destroy but keep the map container content (e.g., loading spinner)
     * gis.destroy({ preserveMapElementContent: true });
     *
     * @example
     * // Calling destroy twice is harmless - the second call only warns
     * gis.destroy();
     * gis.destroy(); // logs "[GIS.destroy] ..." and does nothing
     */
    public destroy(options?:DestroyOptions): void {
        if (!this.Map || !this.Map.olObject) {
            console.warn('[GIS.destroy] destroy() called on an already-destroyed GIS instance. Nothing to do.');
            return;
        }

        try {
            const mapElementID: HTMLElement | string | undefined = this.Map.olObject.getTarget();
            let mapDiv: HTMLElement | null = null;
            if (typeof mapElementID === "string") {
                mapDiv = document.getElementById(mapElementID);
            }
            let preserveElementContent = false;

            if (options && options.preserveMapElementContent) {
                preserveElementContent = options.preserveMapElementContent;
            }

            this.Geolocation.clear();
            this.Control.clear();
            this.Interaction.clear();
            this.Layer.clear();
            this.Projection.clear();
            this.Style.clear();
            this.Localisation.clear();

            this.Map.olObject = undefined;
            this.Map.listeners = {};

            if (!preserveElementContent && mapDiv) {
                while (mapDiv.firstChild) {
                    mapDiv.removeChild(mapDiv.firstChild);
                }
            }
        } catch (error) {
            console.warn('[GIS.destroy] Failed to fully destroy the GIS instance.', error);

            // Make sure the instance is left in a destroyed state so a later call
            // cannot re-enter the teardown with half-cleared modules.
            if (this.Map) {
                this.Map.olObject = undefined;
                this.Map.listeners = {};
            }
        }
    }
}

export default GIS;
